import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { ASSETS } from "@/i18n";
import { OdyneCredit } from "@/components/OdyneCredit";
import { LogOut } from "lucide-react";

export const DashboardShell = ({ children, nav = [], testId }) => {
  const { user, logout } = useAuth();
  const { slug, info } = useTenant();
  const navigate = useNavigate();

  const onLogout = async () => {
    // Navigate BEFORE clearing auth state, otherwise ProtectedRoute
    // intercepts the re-render and pushes to /:slug/login.
    navigate("/", { replace: true });
    await logout();
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s.charAt(0)).join("").slice(0, 2).toUpperCase();

  const subtitle = user?.role === "staff" ? "Staff Area"
    : user?.role === "admin" ? "Titolare"
    : user?.role === "super_admin" ? "Super Admin"
    : "Cliente";

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid={testId}>
      <aside className="fixed top-0 left-0 h-screen w-[260px] hidden lg:flex flex-col justify-between border-r border-black/5 bg-white/70 backdrop-blur-xl px-6 py-8">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-3">
            <img src={ASSETS.logo} alt="AURA"
              className="h-11 w-11 rounded-full object-contain bg-white ring-1 ring-black/5"
              style={{ mixBlendMode: "multiply" }} />
            <div className="leading-tight">
              <div className="font-serif text-2xl tracking-tight text-[#1A1A1A]">AURA</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#737373]">{subtitle}</div>
            </div>
          </Link>
          {info?.name && (
            <div className="mb-9 ml-1 text-xs text-[#4A4A4A] italic">{info.name}</div>
          )}

          <nav className="flex flex-col gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.testId}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition-colors ${
                    isActive ? "bg-[#1A1A1A] text-white" : "text-[#4A4A4A] hover:bg-black/5 hover:text-[#1A1A1A]"
                  }`
                }
              >
                <item.icon size={16} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="border-t border-black/5 pt-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] text-white flex items-center justify-center text-sm font-medium">
              {initials}
            </div>
            <div className="leading-tight flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1A1A1A] truncate">{user?.name}</div>
              <div className="text-xs text-[#737373] truncate">{user?.email}</div>
            </div>
            <button data-testid="logout-button" onClick={onLogout} aria-label="logout"
              className="h-9 w-9 rounded-full border border-black/5 text-[#737373] hover:text-[#1A1A1A] hover:bg-black/5 flex items-center justify-center transition-colors">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-black/5 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={ASSETS.logo} alt="AURA" className="h-9 w-9 rounded-full object-contain bg-white" style={{ mixBlendMode: "multiply" }} />
          <span className="font-serif text-xl text-[#1A1A1A]">AURA</span>
          {info?.name && <span className="text-xs text-[#737373] italic">· {info.name}</span>}
        </Link>
        <button data-testid="logout-button-mobile" onClick={onLogout}
          className="text-sm text-[#737373] inline-flex items-center gap-1.5">
          <LogOut size={14} /> Esci
        </button>
      </header>

      <main className="lg:ml-[260px] px-6 md:px-10 py-10 md:py-14 max-w-[1240px]">
        {children}
        <div className="mt-16 pt-6 border-t border-black/5 flex justify-center lg:justify-start">
          <OdyneCredit />
        </div>
      </main>
    </div>
  );
};
